'use client'

import { isArray } from 'lodash'
import { useCallback, useMemo, useSyncExternalStore } from 'react'

import { useCurrentUser } from '~/data/api/hooks/users'
import { logger } from '~/debug/logger'

const STORAGE_KEY = 'pinnedNavItems'

export type PinnedNavItem = {
  path: string
  pinnedAt: number
}

type UsePinnedNavItemsReturn = {
  pins: PinnedNavItem[]
  isPinned: (path: string) => boolean
  pin: (path: string) => void
  unpin: (path: string) => void
  /**
   * Move the pin at `fromIndex` to `toIndex`. Indices outside the current
   * range or equal to each other are no-ops. Indices reflect the array order
   * returned by `pins`, which is also the display order in the sidebar.
   */
  reorder: (fromIndex: number, toIndex: number) => void
  /**
   * Idempotently seed the user's pin list with `paths` IF the user has never
   * interacted with pins (i.e. the localStorage key is absent). Used to
   * pre-pin a curated set of high-value destinations for first-load users so
   * they discover the feature and land on the most-trafficked screens. A
   * user who explicitly unpins everything (storage key present, value `[]`)
   * will not be re-seeded.
   */
  seedPinsIfMissing: (paths: string[]) => void
}

function isValidItem(item: unknown): item is PinnedNavItem {
  if (typeof item !== 'object' || item === null) return false
  const obj = item as Record<string, unknown>
  return typeof obj.path === 'string' && typeof obj.pinnedAt === 'number'
}

function buildKey(userId: string): string {
  return `${userId}/${STORAGE_KEY}`
}

function readPins(userId: string): PinnedNavItem[] {
  if (typeof window === 'undefined' || !userId) return EMPTY_PINS
  try {
    const stored = window.localStorage.getItem(buildKey(userId))
    if (!stored) return EMPTY_PINS
    const parsed: unknown = JSON.parse(stored)
    if (!isArray(parsed)) return EMPTY_PINS
    const valid = parsed.filter(isValidItem)
    return valid.length === 0 ? EMPTY_PINS : valid
  } catch (error) {
    logger.warn('Failed to read pinned nav items from localStorage', error)
    return EMPTY_PINS
  }
}

function writePins(userId: string, pins: PinnedNavItem[]): void {
  if (typeof window === 'undefined' || !userId) return
  try {
    window.localStorage.setItem(buildKey(userId), JSON.stringify(pins))
  } catch (error) {
    logger.warn('Failed to write pinned nav items to localStorage', error)
  }
}

const EMPTY_PINS: PinnedNavItem[] = []

/**
 * Module-level cache so every consumer of `usePinnedNavItems` for the same user
 * shares one snapshot. Without this, the row-level pin button and the
 * Navigation-level Pinned group end up with separate React state copies and
 * the Pinned group only updates on remount.
 */
const cache = new Map<string, PinnedNavItem[]>()
const subscribers = new Set<() => void>()

function notifySubscribers(): void {
  subscribers.forEach((cb) => cb())
}

// Lazily attach a single `storage` listener for the entire module so we don't
// add one per subscribing component. Cross-tab updates funnel through here:
// when *any* user's pin key changes in another tab, refresh that user's cache
// entry and notify all in-tab subscribers (the per-user `getSnapshot` will
// only return updates relevant to the consumer's own userId).
let storageListenerAttached = false
function ensureStorageListener(): void {
  if (storageListenerAttached || typeof window === 'undefined') return
  storageListenerAttached = true
  window.addEventListener('storage', (event: StorageEvent) => {
    if (!event.key) return
    const [userId, suffix] = event.key.split('/', 2)
    if (!userId || suffix !== STORAGE_KEY) return
    cache.set(userId, readPins(userId))
    notifySubscribers()
  })
}

function getSnapshot(userId: string): PinnedNavItem[] {
  if (!userId) return EMPTY_PINS
  if (!cache.has(userId)) {
    cache.set(userId, readPins(userId))
  }
  return cache.get(userId) ?? EMPTY_PINS
}

function setSnapshot(userId: string, pins: PinnedNavItem[]): void {
  if (!userId) return
  cache.set(userId, pins)
  writePins(userId, pins)
  notifySubscribers()
}

function subscribe(listener: () => void): () => void {
  ensureStorageListener()
  subscribers.add(listener)
  return () => {
    subscribers.delete(listener)
  }
}

/**
 * Manages the user's pinned navigation items. Pins are scoped per-user and
 * persisted to localStorage at `${userId}/pinnedNavItems`. State stays in sync:
 * - across all hook consumers in the same tab (module-level subscription)
 * - across tabs via the `storage` event
 *
 * Returns an empty list and no-op mutators until the current user is known
 * (e.g. during SSR or initial auth load), so the hook is safe to call
 * unconditionally from layout-level components.
 */
export default function usePinnedNavItems(): UsePinnedNavItemsReturn {
  const { synopUser } = useCurrentUser()
  const userId = synopUser?.id ?? ''

  const getSnapshotForUser = useCallback(() => getSnapshot(userId), [userId])
  const getServerSnapshot = useCallback(() => EMPTY_PINS, [])

  const pins = useSyncExternalStore(subscribe, getSnapshotForUser, getServerSnapshot)

  const isPinned = useCallback((path: string) => pins.some((p) => p.path === path), [pins])

  // New pins go to the front so the most recently pinned item is immediately
  // visible at the top of the Pinned group. Array order is the canonical
  // display order — users can manually reorder via `reorder` below.
  const pin = useCallback(
    (path: string) => {
      if (!userId) return
      const current = getSnapshot(userId)
      if (current.some((p) => p.path === path)) return
      setSnapshot(userId, [{ path, pinnedAt: Date.now() }, ...current])
    },
    [userId]
  )

  const unpin = useCallback(
    (path: string) => {
      if (!userId) return
      const current = getSnapshot(userId)
      const next = current.filter((p) => p.path !== path)
      if (next.length === current.length) return
      setSnapshot(userId, next)
    },
    [userId]
  )

  const reorder = useCallback(
    (fromIndex: number, toIndex: number) => {
      if (!userId) return
      if (fromIndex === toIndex) return
      const current = getSnapshot(userId)
      if (fromIndex < 0 || fromIndex >= current.length) return
      if (toIndex < 0 || toIndex >= current.length) return
      const next = [...current]
      const [moved] = next.splice(fromIndex, 1)
      if (!moved) return
      next.splice(toIndex, 0, moved)
      setSnapshot(userId, next)
    },
    [userId]
  )

  // We distinguish "never had pins" (localStorage key absent → seed) from
  // "explicitly unpinned everything" (key present, value `[]` → do nothing)
  // by checking for the raw `null` from `getItem`. `pins` from the hook
  // can't tell these apart, so we read the underlying storage directly.
  const seedPinsIfMissing = useCallback(
    (paths: string[]) => {
      if (!userId) return
      if (typeof window === 'undefined') return
      if (paths.length === 0) return
      try {
        if (window.localStorage.getItem(buildKey(userId)) !== null) return
      } catch (error) {
        logger.warn('Failed to probe pinned nav items localStorage key', error)
        return
      }
      const now = Date.now()
      const seeded: PinnedNavItem[] = paths.map((path, i) => ({ path, pinnedAt: now + i }))
      setSnapshot(userId, seeded)
    },
    [userId]
  )

  return useMemo(
    () => ({ pins, isPinned, pin, unpin, reorder, seedPinsIfMissing }),
    [pins, isPinned, pin, unpin, reorder, seedPinsIfMissing]
  )
}
